
import { Ingredient, Step, CuisineType, FoodType } from "@/types/recipe";
import { Button } from "@/components/ui/button";

interface RecipePreviewProps {
  title: string;
  description: string;
  imagePreview: string | null;
  ingredients: Ingredient[];
  steps: Step[];
  visibility: "public" | "subscription";
  cuisineType: CuisineType | '';
  foodType: FoodType | '';
  onBack: () => void;
}

const RecipePreview = ({
  title,
  description,
  imagePreview,
  ingredients,
  steps,
  visibility,
  cuisineType,
  foodType,
  onBack,
}: RecipePreviewProps) => {
  return (
    <div>
      <div className="mb-8">
        <Button
          variant="outline"
          onClick={onBack}
          className="mb-4"
        >
          ← Back to editing
        </Button>
      </div>
      
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold mb-4">{title || "Untitled Recipe"}</h2>
        
        <div className="flex gap-2 mb-4">
          {cuisineType && (
            <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
              {cuisineType} Cuisine
            </span>
          )}
          {foodType && (
            <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
              {foodType}
            </span>
          )}
        </div>
        
        {imagePreview && (
          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-100 mb-6">
            <img
              src={imagePreview}
              alt="Recipe preview"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        {description && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-2">Description</h3>
            <p className="text-gray-700">{description}</p>
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-2">Ingredients</h3>
          <ul className="list-disc pl-5">
            {ingredients.map((ing, index) => (
              <li key={index} className="text-gray-700">
                {ing.amount} {ing.unit} {ing.name}
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-6">
          <h3 className="text-xl font-semibold mb-2">Steps</h3>
          <ol className="list-decimal pl-5">
            {steps.map((step, index) => (
              <li key={index} className="text-gray-700 mb-2">
                {step.instruction}
                {(step.temperature || step.time) && (
                  <span className="text-sm text-gray-500 block mt-1">
                    {step.temperature && `${step.temperature}`}
                    {step.temperature && step.time && " • "}
                    {step.time && `${step.time}`}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>

        <div className="text-sm text-gray-500">
          This recipe is {visibility === "public" ? "free and public" : "available only to subscribers"}
        </div>
      </div>
    </div>
  );
};

export default RecipePreview;

